import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  CalendarClock, Plus, X, Trash2, Edit2, RefreshCw, Search, Filter,
  CheckCircle2, EyeOff, RotateCcw, AlertTriangle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

type Source = 'endoflife' | 'curated'

interface Cycle {
  cycle: string
  releaseDate: string | null
  eol: string | boolean | null
  support?: string | null
  latest?: string | null
  latestReleaseDate?: string | null
  lts?: boolean
  link?: string | null
}

interface EOLProduct {
  id: number
  slug: string
  name: string
  category: string
  icon: string
  source: Source
  note: string
  cycles: Cycle[]
  enabled: boolean
  position: number
  created_at: string | null
  updated_at: string | null
}

interface ListResp {
  count: number
  enabled: number
  by_category: Record<string, number>
  sources: { endoflife: number; curated: number }
  products: EOLProduct[]
}

const CATEGORIES = ['runtime', 'framework', 'database', 'infra', 'os', 'browser']
const CAT_COLOR: Record<string, string> = {
  runtime: 'text-blue',
  framework: 'text-violet',
  database: 'text-emerald',
  infra: 'text-amber-light',
  os: 'text-cyan',
  browser: 'text-orange',
}

// ── Form ─────────────────────────────────────────────────────────────────────

function ProductForm({
  initial, onSave, onCancel,
}: {
  initial?: Partial<EOLProduct>
  onSave: (p: any) => void
  onCancel: () => void
}) {
  const [slug, setSlug] = useState(initial?.slug || '')
  const [name, setName] = useState(initial?.name || '')
  const [category, setCategory] = useState(initial?.category || 'infra')
  const [icon, setIcon] = useState(initial?.icon || '')
  const [source, setSource] = useState<Source>((initial?.source as Source) || 'endoflife')
  const [note, setNote] = useState(initial?.note || '')
  const [cyclesJson, setCyclesJson] = useState(JSON.stringify(initial?.cycles || [], null, 2))
  const [enabled, setEnabled] = useState(initial?.enabled !== false)
  const [position, setPosition] = useState(initial?.position ?? 100)
  const [cycleError, setCycleError] = useState<string | null>(null)

  const handleSave = () => {
    let cycles: any[] = []
    if (source === 'curated') {
      try {
        const parsed = JSON.parse(cyclesJson || '[]')
        if (!Array.isArray(parsed)) throw new Error('must be an array')
        cycles = parsed
      } catch (e: any) {
        setCycleError(e.message || 'invalid JSON')
        return
      }
    }
    setCycleError(null)
    onSave({ slug, name, category, icon, source, note, cycles, enabled, position })
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Slug *</label>
          <input value={slug} onChange={e => setSlug(e.target.value)} placeholder="e.g. windows-server" className="input py-1.5 text-[12px] w-full font-mono" />
          <p className="text-[9px] text-text-muted mt-1">For endoflife, must match the API path at endoflife.date.</p>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Display name *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Windows Server" className="input py-1.5 text-[12px] w-full" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} title="Category" className="input py-1.5 text-[12px] w-full">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Source</label>
          <div className="flex gap-1">
            {(['endoflife', 'curated'] as Source[]).map(s => (
              <button type="button" key={s} onClick={() => setSource(s)}
                className={clsx('flex-1 py-1 text-[11px] rounded border transition-colors capitalize',
                  source === s
                    ? 'bg-blue/20 border-blue/40 text-blue-light'
                    : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
                )}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Position</label>
          <input type="number" value={position} onChange={e => setPosition(Number(e.target.value))} title="Sort order within category" className="input py-1.5 text-[12px] w-full font-mono" />
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Note (curated only)</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
          placeholder="Context shown on the EOL page (vendor support window caveats, etc.)"
          className="input py-1.5 text-[12px] w-full" />
      </div>

      {source === 'curated' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Cycles (JSON)</label>
            <span className="text-[9px] text-text-muted">{'[{cycle, releaseDate, eol, latest, lts}]'}</span>
          </div>
          <textarea value={cyclesJson} onChange={e => { setCyclesJson(e.target.value); setCycleError(null) }} rows={10}
            className="input py-1.5 text-[10px] w-full font-mono leading-snug"
            spellCheck={false} />
          {cycleError && (
            <p className="text-[10px] text-rose mt-1 flex items-center gap-1">
              <AlertTriangle size={10} /> {cycleError}
            </p>
          )}
          <p className="text-[9px] text-text-muted mt-1">
            Each cycle: cycle name (e.g. "24"), releaseDate (YYYY-MM-DD), eol (YYYY-MM-DD or false for no-EOL), latest, lts (bool).
          </p>
        </div>
      )}

      <label className="flex items-center gap-2 text-[11px] text-text-secondary cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="accent-blue" />
        Enabled (shown on the EOL calendar)
      </label>

      <div className="flex items-center gap-2 pt-1">
        <button type="button" onClick={handleSave} disabled={!slug.trim() || !name.trim()} className="btn-primary text-xs">
          Save
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function EOLManagePage() {
  const navigate = useNavigate()
  const [data, setData] = useState<ListResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<EOLProduct | null>(null)
  const [searchQ, setSearchQ] = useState('')
  const [catFilter, setCatFilter] = useState<string>('')
  const [sourceFilter, setSourceFilter] = useState<Source | ''>('')
  const [showDisabled, setShowDisabled] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/eol/products')
      setData(data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const save = async (p: any) => {
    if (editing) await api.put(`/api/eol/products/${editing.id}`, p)
    else         await api.post('/api/eol/products', p)
    setShowForm(false); setEditing(null)
    fetchData()
  }

  const remove = async (id: number) => {
    await api.delete(`/api/eol/products/${id}`)
    fetchData()
  }

  const toggle = async (id: number) => {
    await api.post(`/api/eol/products/${id}/toggle`)
    fetchData()
  }

  const resetDefaults = async () => {
    if (!confirm('This wipes all your edits and reloads the hardcoded default list. Continue?')) return
    await api.post('/api/eol/products/reset-defaults')
    fetchData()
  }

  const filtered = useMemo(() => {
    if (!data) return []
    return data.products.filter(p => {
      if (!showDisabled && !p.enabled) return false
      if (catFilter && p.category !== catFilter) return false
      if (sourceFilter && p.source !== sourceFilter) return false
      if (searchQ) {
        const q = searchQ.toLowerCase()
        if (!(`${p.slug} ${p.name} ${p.note}`.toLowerCase().includes(q))) return false
      }
      return true
    })
  }, [data, searchQ, catFilter, sourceFilter, showDisabled])

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }
  if (!data) return <div className="text-text-muted text-sm">No data</div>

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <CalendarClock size={20} className="text-cyan" /> EOL Products — Manage
          </h1>
          <p className="text-[10px] text-text-secondary">
            {data.enabled}/{data.count} enabled · {data.sources.endoflife} from endoflife.date · {data.sources.curated} curated.
            Changes apply to the <button type="button" onClick={() => navigate('/app/eol')} className="text-blue-light hover:underline">EOL calendar</button> immediately.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={resetDefaults} className="btn-ghost text-xs text-amber" title="Wipe custom entries and re-seed defaults">
            <RotateCcw size={12} /> Reset defaults
          </button>
          <button type="button" onClick={fetchData} className="btn-ghost text-xs" title="Refresh" aria-label="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <button type="button" onClick={() => { setEditing(null); setShowForm(true) }} className="btn-primary text-xs">
            <Plus size={12} /> New product
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {CATEGORIES.map(c => (
          <div key={c} className="card p-2.5">
            <div className={clsx('text-[10px] uppercase tracking-wider font-semibold', CAT_COLOR[c] || 'text-text-muted')}>
              {c}
            </div>
            <div className="text-base font-bold text-text-primary">{data.by_category[c] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <Filter size={12} className="text-text-muted" />
        <div className="flex gap-1 flex-wrap">
          <button type="button" onClick={() => setCatFilter('')} className={clsx('px-2 py-1 text-[10px] rounded border',
            !catFilter ? 'bg-blue/10 border-blue/30 text-blue-light' : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary')}>
            All cats
          </button>
          {CATEGORIES.map(c => (
            <button type="button" key={c} onClick={() => setCatFilter(c)} className={clsx('px-2 py-1 text-[10px] rounded border capitalize',
              catFilter === c ? 'bg-blue/10 border-blue/30 text-blue-light' : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary')}>
              {c}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {(['endoflife', 'curated'] as Source[]).map(s => (
            <button type="button" key={s} onClick={() => setSourceFilter(sourceFilter === s ? '' : s)}
              className={clsx('px-2 py-1 text-[10px] rounded border capitalize',
                sourceFilter === s ? 'bg-emerald/10 border-emerald/30 text-emerald' : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary')}>
              {s}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-[10px] text-text-secondary cursor-pointer">
          <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} className="accent-blue" />
          Include disabled
        </label>
        <div className="flex items-center gap-1 ml-auto">
          <Search size={12} className="text-text-muted" />
          <input type="search" value={searchQ} onChange={e => setSearchQ(e.target.value)}
            placeholder="Search slug / name / note…"
            className="input py-1 text-[11px] w-52" />
        </div>
      </div>

      {/* Product table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-surface-raised/50">
              <tr className="border-b border-border">
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 w-8" aria-label="Enabled" />
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Slug</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Name</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Category</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Source</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Cycles</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Pos</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 w-24" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className={clsx('border-b border-border/50 hover:bg-surface-raised/50 transition-colors',
                  !p.enabled && 'opacity-50')}>
                  <td className="py-1.5 px-3">
                    <button type="button" onClick={() => toggle(p.id)} className="text-text-muted hover:text-text-primary" title={p.enabled ? 'Click to disable' : 'Click to enable'} aria-label={p.enabled ? 'Disable' : 'Enable'}>
                      {p.enabled
                        ? <CheckCircle2 size={14} className="text-emerald" />
                        : <EyeOff size={14} />
                      }
                    </button>
                  </td>
                  <td className="py-1.5 px-3 font-mono text-[10px] text-text-secondary">{p.slug}</td>
                  <td className="py-1.5 px-3 text-[11px] font-semibold text-text-primary">{p.name}</td>
                  <td className={clsx('py-1.5 px-3 text-[10px] capitalize', CAT_COLOR[p.category])}>{p.category}</td>
                  <td className="py-1.5 px-3">
                    <span className={clsx('text-[9px] px-1.5 py-0.5 rounded border capitalize',
                      p.source === 'curated'
                        ? 'bg-emerald/10 border-emerald/30 text-emerald'
                        : 'bg-blue/10 border-blue/30 text-blue-light')}>
                      {p.source}
                    </span>
                  </td>
                  <td className="py-1.5 px-3 text-right text-[10px] font-mono text-text-secondary">
                    {p.source === 'curated' ? p.cycles.length : '—'}
                  </td>
                  <td className="py-1.5 px-3 text-right text-[10px] font-mono text-text-muted">{p.position}</td>
                  <td className="py-1.5 px-3 text-right">
                    <button type="button" onClick={() => { setEditing(p); setShowForm(true) }} className="btn-ghost p-1" title="Edit" aria-label="Edit">
                      <Edit2 size={11} />
                    </button>
                    <button type="button"
                      onClick={() => { if (confirm(`Delete ${p.slug}?`)) remove(p.id) }}
                      className="btn-ghost p-1 text-rose" title="Delete" aria-label="Delete">
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-8 text-[11px] text-text-muted">No products match these filters.</div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
              <h2 className="text-sm font-bold text-text-primary">
                {editing ? `Edit ${editing.slug}` : 'New EOL product'}
              </h2>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null) }} title="Close" aria-label="Close" className="text-text-muted hover:text-text-primary">
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <ProductForm
                initial={editing || undefined}
                onSave={save}
                onCancel={() => { setShowForm(false); setEditing(null) }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
